import { defineStore } from 'pinia';
import { unlockBodyScroll } from '~/utilities/ui-utilities';
import { useSiteStore } from '~/stores/site.store';
import type {
  AuthStoreInterface,
  LogoutOptions,
  TComplianceCheckType,
} from '~/interfaces/stores/auth.store.interface';
import type { AccountModals } from '~/interfaces/dto/general/modals';
import type { IAccountBalances } from '~/interfaces/dto/wallet/response';
import {
  ERegistrationStatus,
  type IUserInfoExtended,
} from '~/interfaces/dto/auth/response';
import type {
  IPeriod,
  IRewardsBalanceResponse,
  IRewardsConfigResponse,
} from '~/interfaces/dto/rewards/response';
import { useServices } from '~/composables/useServices';
import type { CasinoBonus } from '~/interfaces/dto/bonus/response';
import { globalKeys } from 'assets/data/global-keys';
import type { IPromotionGenericWheelData } from '~/interfaces/dto/promotions/response';
import { useQueryActions } from '~/utilities/query-actions';
import { bitwiseCheck } from '~/utilities/data-transformers';
import { useRgStore } from './responsibleGaming.store';
import { useResponsibleGamingInit } from '~/composables/useCheckRestrictions';
import usePersonalised from '~/composables/usePersonalised';
import useRestoreSession from '~/composables/useRestoreSession';

export const useAuthStore = defineStore('auth', {
  state: (): AuthStoreInterface => {
    return {
      userId: '',
      currentUser: null,
      isLoggedIn: false,
      currentAccessToken: '',
      currentRefreshToken: '',
      displayAccountModal: false,
      welcomeOffer: false,
      displayBalances: true,
      userBalances: {},
      compliance: true,
      partialPostReg: true,
      accountModalPage: null,
      activeBonus: {},
      displayBonusBalance: false,
      passwordResetMobileNumber: null,
      availableSpins: [],
      rewardsBalance: {
        amount: 0,
        lifeTimePointsEarned: 0,
        totalPointsConverted: 0,
      },
      rewardsConfig: null,
      summaryYearConfig: [],
    };
  },
  actions: {
    async initAuth() {
      const { $storageService, $authService } = useServices();
      try {
        const accessToken = $storageService.getCookie<string>({
          key: globalKeys.authKeys.accessToken,
        });

        const refreshToken = $storageService.getCookie<string>({
          key: globalKeys.authKeys.refreshToken,
        });

        if (!!accessToken && !!refreshToken) {
          const userId = $storageService.getCookie<string>({
            key: globalKeys.authKeys.userId,
          });
          this.setAccessToken(accessToken);
          this.setRefreshToken(refreshToken);
          this.setUserId(userId);
          await this.setLoggedIn();
        } else if (!!refreshToken && !accessToken) {
          this.setRefreshToken(refreshToken);
          await $authService.refreshToken();
          await this.setLoggedIn();
        } else {
          this.logout();
        }
      } catch (e) {
        console.error(e);
      }
    },
    setUser(data: IUserInfoExtended) {
      try {
        this.currentUser = data;
        this.setUserId(data?.id);
      } catch (e) {
        console.error('failed to set user', e.toString());
      }
    },
    setUserBalances(data: IAccountBalances) {
      this.userBalances = data;
    },
    setBalancesVisibility() {
      const { $storageService } = useServices();
      const visibility = $storageService.getLocalStorage({
        key: globalKeys.authKeys.hideBalances,
      });

      const transformedVisibility =
        visibility === true || visibility === 'true'
          ? true
          : !(visibility === false || visibility === 'false');

      this.toggleBalances(transformedVisibility);
    },
    toggleBalances(data: boolean) {
      const { $storageService } = useServices();
      this.displayBalances = data;
      $storageService.setLocalStorage({
        key: globalKeys.authKeys.hideBalances,
        data,
      });
    },
    async setLoggedIn() {
      const {
        $walletService,
        $authService,
        $marketingService,
        $gameService,
        $jackpotService,
      } = useServices();
      const { $enabled } = useNuxtApp();
      const rg = useRgStore();
      if (!this.currentAccessToken) this.isLoggedIn = false;
      else {
        const site = useSiteStore();
        site.deactivateModal();
        unlockBodyScroll();
        this.isLoggedIn = true;
        this.setBalancesVisibility();
        if (!site.isWebview) {
          await Promise.allSettled([
            await $authService.getUserExtended(),
            await $walletService.fetchBalances(),
          ]);
          // const personalised = usePersonalised();
          // if (!personalised.getPersonalisedData('recommended')?.length)
          await $gameService.fetchPersonalisedGames();
          // else personalised.setGameStore();

          const route = useRoute();

          if (Object.keys(route.query).length > 0) await useQueryActions(route);

          await $marketingService.fetchToasts();

          await useResponsibleGamingInit();
          await useRestoreSession(this.userId);
          if (!this.currentUser.isRegistrationComplete && rg.hasPartialRegLock)
            site.activateModal('acknowledge-partial-reg');

          if ($enabled('jackpots.enablefixedjackpot')) {
            await $jackpotService.fetchFixedJackpotEvents();
          }
        }
      }
    },
    setAccessToken(access_token: string | undefined) {
      try {
        this.currentAccessToken = access_token;
        const { $storageService } = useServices();
        $storageService.setCookie({
          key: globalKeys.authKeys.accessToken,
          data: access_token,
          expiry: 1,
        });
      } catch (e) {
        console.error('failed to set access token', e.toString());
      }
    },
    setRefreshToken(refresh_token: string | undefined) {
      try {
        this.currentRefreshToken = refresh_token;
        const { $storageService } = useServices();
        $storageService.setCookie({
          key: globalKeys.authKeys.refreshToken,
          data: refresh_token,
          expiry: 7,
        });
      } catch (e) {
        console.error('failed to set refresh token', e.toString());
      }
    },
    setUserId(userId: string | undefined) {
      this.userId = userId;
      const { $storageService } = useServices();
      $storageService.setCookie({
        key: globalKeys.authKeys.userId,
        data: userId,
        expiry: 7,
      });
      window?.dtrum?.identifyUser(userId);
    },
    setPartialPostReg(flag: boolean) {
      this.partialPostReg = flag;
    },
    logout(
      { redirect, path, reload }: LogoutOptions = {
        redirect: false,
        path: '/',
        reload: false,
      },
    ) {
      const site = useSiteStore();
      const { $storageService, $marketingService } = useServices();
      const { $destroyLiveChat, $trackEvent } = useNuxtApp();

      const currentPushToken = $storageService.getSessionStorage({
        key: globalKeys.pushNotificationKeys.pushTokenKey,
      });

      if (typeof currentPushToken === 'string' && !!currentPushToken) {
        $storageService.removeSessionStorage({
          key: globalKeys.pushNotificationKeys.pushTokenKey,
        });
        $marketingService.deregisterPushToken(currentPushToken);
      }

      Object.values(globalKeys.authKeys).forEach((key) => {
        $storageService.removeUniversalStorage({ key });
      });
      if (site.getFeatureFlags.get('responsiblegaming.sessionlimit')) {
        const persistTimer: any =
          $storageService.getLocalStorage<boolean | number>({
            key: globalKeys.authKeys.sessionTimer,
          }) || false;
        if (persistTimer) {
          $storageService.removeLocalStorage({
            key: globalKeys.authKeys.sessionTimer,
          });
        }
      }
      this.toggleAccountModal(false);
      $trackEvent('Account', 'MY_ACCOUNT_LOGOUT');
      const personalised = usePersonalised();
      //Clear Recently Played
      personalised.setPersonalisedData('recentlyPlayed', []);
      //clear favorites
      personalised.setPersonalisedData('favorites', []);
      unlockBodyScroll();
      this.$reset();

      if (!!window?.navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (const registration of registrations) {
            if (
              registration.active?.scriptURL?.includes('firebase-messaging-sw')
            )
              registration.unregister();
          }
        });
      }

      if (redirect) {
        window.location.href = !!path ? path : '/';
        return;
      }
      if (reload) {
        const router = useRouter();
        router.go(0);
        return;
      }
      $destroyLiveChat();
    },
    toggleAccountModal(active: boolean) {
      this.displayAccountModal = active;
      const { $marketingService } = useServices();
      if (!active) {
        $marketingService.fetchToasts();
      }
    },
    setAccountModalPage(page: AccountModals) {
      if (!!page) this.toggleAccountModal(true);
      this.accountModalPage = page;
    },
    setActiveBonus(bonus: CasinoBonus) {
      this.activeBonus = bonus;
    },
    setPasswordResetMobileNumber(mobileNumber: string) {
      this.passwordResetMobileNumber = mobileNumber;
    },
    setAvailableSpins(spins: Partial<IPromotionGenericWheelData>[]) {
      this.availableSpins = spins;
    },
    setRewardsConfig(config: Partial<IRewardsConfigResponse>) {
      this.rewardsConfig = config;
    },
    setRewardsBalance(balance: Partial<IRewardsBalanceResponse>) {
      this.rewardsBalance = balance;
    },
    setSummaryYearConfig(periodSummary: Partial<IPeriod>[]) {
      this.summaryYearConfig = periodSummary;
    },
  },
  getters: {
    loggedIn(state): boolean {
      return state?.isLoggedIn;
    },
    user(state): Partial<IUserInfoExtended> {
      return state?.currentUser;
    },
    access_token(state): string {
      return state?.currentAccessToken;
    },
    user_id(state): string {
      return state?.userId;
    },
    getWelcomeOffer(state): boolean {
      return state?.welcomeOffer;
    },
    accountBalances(state): IAccountBalances {
      return state.userBalances;
    },
    displayAccountBalances(state): boolean {
      return state.displayBalances;
    },
    accountModal(state): boolean {
      return state.displayAccountModal;
    },
    getAccountModalPage(state): AccountModals {
      return state.accountModalPage;
    },
    getIdDocumentStatus(state) {
      return state.currentUser?.proofOfIdDocumentStatus;
    },
    getBankDocumentStatus(state) {
      return state.currentUser?.proofOfBankDocumentStatus;
    },
    getSupportingDocumentStatus(state) {
      return state.currentUser?.proofOfResidenceDocumentStatus;
    },
    getComplianceStatus(state) {
      return state.currentUser?.compliance?.complianceStatus;
    },
    getSumsubRequired() {
      return this.getComplianceCheckRequired === 'sumsub';
    },
    getCbpRequired() {
      return this.getComplianceCheckRequired === 'cpb';
    },
    getComplianceCheckRequired(state): TComplianceCheckType {
      const { $enabled } = useNuxtApp();

      const compliance = state.currentUser?.compliance;
      const sumsubRequired =
        $enabled('security.sumsubenabled') &&
        (compliance?.complianceStatus === 0 ||
          compliance?.complianceStatus === 512);

      if (!compliance) return 'none';
      if (this.getIsUserCompliant) return 'none';
      if (sumsubRequired) return 'sumsub';
      if ($enabled('myaccount.enablecompliance') && !this.getIsUserCompliant)
        return 'cpb';

      return 'none';
    },
    getIsUserCompliant(state) {
      const { $enabled } = useNuxtApp();
      const compliance = state.currentUser?.compliance;

      if ($enabled('myaccount.enablecompliance')) {
        if (
          compliance?.complianceStatus === 1 &&
          compliance?.requiredComplianceStatus === 1
        )
          return true;
        else if (
          compliance?.complianceStatus === 0 ||
          compliance?.complianceStatus === 512
        )
          return false;
        return bitwiseCheck(
          state.currentUser?.compliance?.complianceStatus,
          state.currentUser?.compliance?.requiredComplianceStatus,
        );
      } else return true;
    },
    getPartialPostReg(state) {
      return state.partialPostReg;
    },
    getIsRegistrationComplete(state) {
      const { $enabled } = useNuxtApp();
      if ($enabled('myaccount.enablepartialreg')) {
        return state.currentUser?.isRegistrationComplete;
      } else if (
        !state.currentUser?.isRegistrationComplete &&
        state.currentUser?.registrationStatus === ERegistrationStatus.PENDING
      ) {
        return this.getIsUserCompliant;
      } else
        return (
          state.currentUser?.isRegistrationComplete && this.getIsUserCompliant
        );
    },
    getActiveBonus(state) {
      return state.activeBonus;
    },
    getAvailableSpins(state) {
      return state.availableSpins;
    },
    getCurrentSpin(state) {
      return (
        state.availableSpins?.filter(
          // (as) => as.isActive && as.unusedSpinCount > 0,
          (as) => as?.unusedSpinCount > 0,
        )?.[0] || null
      );
    },
    getRewardsConfig(state) {
      return state.rewardsConfig;
    },
    getRewardsBalance(state) {
      return state.rewardsBalance;
    },
    getSummaryYearConfig(state) {
      return state.summaryYearConfig;
    },
    getMobileNumber(state) {
      return state.currentUser?.username;
    },
    getCanLaunch(state) {
      const { $enabled } = useNuxtApp();
      const rgStore = useRgStore();

      if (this.loggedIn && !!state.currentUser) {
        if (
          (!this.getIsUserCompliant &&
            $enabled('myaccount.enablecompliance') &&
            !$enabled('myaccount.enablepartialreg')) ||
          (!!rgStore?.restrictionPermissions &&
            !rgStore?.restrictionPermissions?.allowedWager)
        ) {
          return false;
        }
        return true;
      }
      return true;
    },
  },
});
